import React, { useState, useEffect } from "react";
import { Button, Spin, Pagination, Tag, Empty, message } from "antd";
import {
  ArrowLeft,
  Package,
  Calendar,
  CreditCard,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
} from "lucide-react";
import { API_METHODS } from "../../constants";
import { isEmpty } from "../../generalFunctions";
import useApi from "../../hooks/useApi";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";
import { useNavigate, useSearchParams } from "react-router-dom";

const MyOrders = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [userType, setUserType] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // API hook for orders
  const [ordersResponse, ordersRequest] = useApi(
    "/user/orders",
    null,
    API_METHODS.GET
  );

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const guestUserId = searchParams.get("guest_user_id");
  const [messageApi, contextHolder] = message.useMessage();

  // Check user authentication status
  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    const guestUser = localStorage.getItem("guestUser");
    const userData = localStorage.getItem("user");

    if (authToken && userData) {
      setUserType("authenticated");
      setCurrentUser(JSON.parse(userData));
    } else if (guestUser || guestUserId) {
      setUserType("guest");
      if (guestUser) {
        setCurrentUser(JSON.parse(guestUser));
      } else if (guestUserId) {
        setCurrentUser({ id: guestUserId });
      }
    } else {
      messageApi.open({
        type: "info",
        content: `🙏 Please login or continue as guest to view your orders.`,
        duration: 2,
      });
      navigate("/services");
      return;
    }
  }, [navigate, guestUserId]);

  // Fetch orders when user type is determined
  useEffect(() => {
    if (userType && currentUser) {
      fetchOrders(1);
    }
  }, [userType, currentUser]);

  const fetchOrders = (page) => {
    if (userType === "authenticated") {
      ordersRequest(null, API_METHODS.GET, `/user/orders?pageNumber=${page}`);
    } else if (userType === "guest") {
      ordersRequest(
        null,
        API_METHODS.GET,
        `/user/orders/guest/${currentUser.id}?pageNumber=${page}`
      );
    }
    setCurrentPage(page);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  };

  const formatFullDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
        return "green";
      case "completed":
        return "blue";
      case "pending":
        return "orange";
      case "payment_failed":
        return "red";
      case "cancelled":
        return "red";
      default:
        return "gray";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
        return <CheckCircle className="h-3 w-3" />;
      case "completed":
        return <CheckCircle className="h-3 w-3" />;
      case "pending":
        return <Clock className="h-3 w-3" />;
      case "payment_failed":
        return <XCircle className="h-3 w-3" />;
      case "cancelled":
        return <XCircle className="h-3 w-3" />;
      default:
        return <AlertCircle className="h-3 w-3" />;
    }
  };

  const handleViewDetails = (orderId) => {
    if (userType === "guest") {
      navigate(`/orders/${orderId}?guest_user_id=${currentUser.id}`);
    } else {
      navigate(`/orders/${orderId}`);
    }
  };

  const handlePageChange = (page) => {
    fetchOrders(page);
  };

  const handleBookService = () => {
    navigate("/services");
  };

  // Loading state
  if (ordersResponse?.isLoading && currentPage === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-base lg:text-lg text-gray-600">
            Loading your orders...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (isEmpty(ordersResponse)) {
    return (
      <>
        <HeaderComponent />
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-8 lg:py-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-white rounded-xl lg:rounded-2xl p-8 lg:p-12 text-center shadow-xl">
              <div className="text-4xl lg:text-6xl mb-4">⚠️</div>
              <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4">
                Unable to load orders
              </h2>
              <p className="text-gray-600 mb-6 text-sm lg:text-base">
                There was a problem loading your orders. Please try again.
              </p>
              <Button
                type="primary"
                size="large"
                onClick={() => window.location.reload()}
                className="bg-gradient-to-r from-orange-500 to-red-500 border-none h-12"
              >
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const orders = ordersResponse?.data || [];
  const pagination = ordersResponse?.pagination || {};

  return (
    <>
      {contextHolder}
      <HeaderComponent />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-8 lg:py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header - Matching Order Details Style */}
          <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 mb-6 lg:mb-8">
            {/* Back Button */}
            <button
              onClick={() => navigate("/services")}
              className="flex items-center text-gray-600 hover:text-orange-600 transition-all mb-4 p-2 rounded-lg hover:bg-orange-50"
            >
              <ArrowLeft className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
              <span className="text-sm lg:text-base font-medium">
                Back to Services
              </span>
            </button>

            {/* Header Content */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-start space-x-3 lg:space-x-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-lg lg:text-xl flex-shrink-0">
                  🪔
                </div>
                <div>
                  <h1 className="text-xl lg:text-3xl font-bold text-gray-800">
                    My Orders
                    {orders.length > 0 && (
                      <span className="text-base lg:text-xl text-gray-500 ml-2">
                        ({pagination.count || orders.length})
                      </span>
                    )}
                  </h1>
                  <p className="text-sm lg:text-base text-gray-600 mt-1">
                    Track your temple service bookings
                  </p>
                </div>
              </div>

              {/* Guest Badge */}
              {userType === "guest" && (
                <div className="self-start sm:self-center">
                  <div className="bg-blue-100 border border-blue-200 rounded-lg px-3 py-2">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">
                        Guest Orders
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Orders List - Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                {orders.length === 0 ? (
                  <div className="p-8">
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <div className="text-center">
                          <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-2">
                            No orders found
                          </h3>
                          <p className="text-gray-600 mb-6 text-sm lg:text-base">
                            {userType === "guest"
                              ? "No orders found for this guest account."
                              : "You haven't placed any temple service orders yet."}
                          </p>
                          <Button
                            type="primary"
                            size="large"
                            onClick={handleBookService}
                            className="bg-gradient-to-r from-orange-500 to-red-500 border-none h-12 px-6"
                          >
                            Start Booking Services
                          </Button>
                        </div>
                      }
                    />
                  </div>
                ) : (
                  <>
                    {/* Compact Orders List */}
                    <div className="divide-y divide-gray-100">
                      {orders.map((order) => (
                        <div
                          key={order.id}
                          className="p-4 hover:bg-gray-50 transition-colors"
                        >
                          {/* Mobile Layout */}
                          <div className="block sm:hidden space-y-2">
                            {/* Row 1: Order number + Status */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Package className="h-4 w-4 text-orange-500 flex-shrink-0" />
                                <span className="font-semibold text-gray-800 text-sm">
                                  #{order.order_number.split("-").pop()}
                                </span>
                              </div>
                              <Tag
                                color={getStatusColor(order.order_status)}
                                icon={getStatusIcon(order.order_status)}
                                className="text-xs px-2 py-0.5"
                              >
                                {order.order_status?.toUpperCase()}
                              </Tag>
                            </div>

                            {/* Row 2: Service date + Amount */}
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center space-x-1 text-gray-600">
                                <Calendar className="h-3 w-3" />
                                <span>
                                  {formatFullDate(order.booking_date)}
                                </span>
                              </div>
                              <div className="font-bold text-orange-600">
                                {formatCurrency(order.total_amount)}
                              </div>
                            </div>

                            {/* Row 3: Payment status + Action */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-1 text-xs">
                                <div
                                  className={`w-2 h-2 rounded-full ${
                                    order.payment_status === "completed"
                                      ? "bg-green-500"
                                      : "bg-yellow-500"
                                  }`}
                                />
                                <span
                                  className={`${
                                    order.payment_status === "completed"
                                      ? "text-green-600"
                                      : "text-yellow-600"
                                  }`}
                                >
                                  {order.payment_status === "completed"
                                    ? "Paid"
                                    : "Pending"}
                                </span>
                              </div>
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => handleViewDetails(order.id)}
                                className="bg-gradient-to-r from-orange-500 to-red-500 border-none h-7 px-3 text-xs"
                                icon={<Eye className="h-3 w-3" />}
                              >
                                View
                              </Button>
                            </div>

                            {/* Notes if present */}
                            {order.notes && (
                              <div className="text-xs text-gray-500 italic bg-gray-50 rounded px-2 py-1">
                                "{order.notes}"
                              </div>
                            )}
                          </div>

                          {/* Desktop Layout */}
                          <div className="hidden sm:block">
                            <div className="flex items-center justify-between">
                              {/* Left section */}
                              <div className="flex items-center space-x-4 flex-1">
                                <div className="flex items-center space-x-2">
                                  <Package className="h-4 w-4 text-orange-500" />
                                  <span className="font-semibold text-gray-800">
                                    #{order.order_number}
                                  </span>
                                </div>

                                <Tag
                                  color={getStatusColor(order.order_status)}
                                  icon={getStatusIcon(order.order_status)}
                                  className="text-xs"
                                >
                                  {order.order_status?.toUpperCase()}
                                </Tag>

                                <div className="text-sm text-gray-600 hidden md:block">
                                  Placed: {formatDate(order.created_on)}
                                </div>
                              </div>

                              {/* Middle section */}
                              <div className="flex items-center space-x-6 flex-1 justify-center">
                                <div className="flex items-center space-x-1 text-sm">
                                  <Calendar className="h-4 w-4 text-green-600" />
                                  <span className="text-gray-700">
                                    {formatFullDate(order.booking_date)}
                                  </span>
                                </div>

                                <div className="flex items-center space-x-1 text-sm">
                                  <CreditCard className="h-4 w-4 text-orange-600" />
                                  <span className="font-bold text-orange-600">
                                    {formatCurrency(order.total_amount)}
                                  </span>
                                </div>

                                <div className="flex items-center space-x-1 text-sm">
                                  <div
                                    className={`w-2 h-2 rounded-full ${
                                      order.payment_status === "completed"
                                        ? "bg-green-500"
                                        : "bg-yellow-500"
                                    }`}
                                  />
                                  <span
                                    className={`text-xs ${
                                      order.payment_status === "completed"
                                        ? "text-green-600"
                                        : "text-yellow-600"
                                    }`}
                                  >
                                    {order.payment_status === "completed"
                                      ? "Paid"
                                      : "Pending"}
                                  </span>
                                </div>
                              </div>

                              {/* Right section */}
                              <div className="flex items-center space-x-3">
                                {order.notes && (
                                  <div className="text-xs text-gray-500 italic max-w-24 truncate hidden lg:block">
                                    "{order.notes}"
                                  </div>
                                )}
                                <Button
                                  type="primary"
                                  size="middle"
                                  onClick={() => handleViewDetails(order.id)}
                                  className="bg-gradient-to-r from-orange-500 to-red-500 border-none"
                                  icon={<Eye className="h-4 w-4" />}
                                >
                                  View Details
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {pagination.maxPages > 1 && (
                      <div className="p-4 border-t border-gray-100">
                        <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                          <div className="text-sm text-gray-600 order-2 sm:order-1">
                            Showing {(currentPage - 1) * 10 + 1}-
                            {Math.min(currentPage * 10, pagination.count)} of{" "}
                            {pagination.count} orders
                          </div>
                          <Pagination
                            current={currentPage}
                            total={pagination.count}
                            pageSize={10}
                            onChange={handlePageChange}
                            showSizeChanger={false}
                            showQuickJumper={false}
                            className="order-1 sm:order-2"
                            size="small"
                            responsive={true}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Sidebar - Desktop Only */}
            <div className="lg:col-span-1 hidden lg:block space-y-6">
              {/* Quick Stats */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <Package className="h-5 w-5 mr-2 text-orange-500" />
                  Order Summary
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Total Orders</span>
                    <span className="font-bold text-orange-600">
                      {pagination.count || orders.length}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Completed</span>
                    <span className="font-bold text-green-600">
                      {
                        orders.filter((o) => o.order_status === "completed")
                          .length
                      }
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">In Progress</span>
                    <span className="font-bold text-blue-600">
                      {
                        orders.filter((o) => o.order_status === "confirmed")
                          .length
                      }
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Pending</span>
                    <span className="font-bold text-yellow-600">
                      {
                        orders.filter((o) => o.order_status === "pending")
                          .length
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleBookService}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 border-none"
                  >
                    Book New Service
                  </Button>
                  <Button
                    size="large"
                    onClick={() => navigate("/contact")}
                    className="w-full"
                  >
                    Contact Support
                  </Button>
                </div>
              </div>

              {/* Help Section */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4">
                <h3 className="text-base font-semibold text-gray-800 mb-2">
                  Need Help? 🙏
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Contact our support team for assistance with your orders.
                </p>
                <Button
                  size="small"
                  onClick={() => window.open("tel:+919699588995", "_self")}
                  className="w-full"
                >
                  📞 Call Support
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile Quick Stats */}
          {orders.length > 0 && (
            <div className="lg:hidden mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 text-center shadow-lg">
                <div className="text-xl font-bold text-orange-600">
                  {pagination.count || orders.length}
                </div>
                <div className="text-xs text-gray-600">Total</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center shadow-lg">
                <div className="text-xl font-bold text-green-600">
                  {orders.filter((o) => o.order_status === "completed").length}
                </div>
                <div className="text-xs text-gray-600">Completed</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center shadow-lg">
                <div className="text-xl font-bold text-blue-600">
                  {orders.filter((o) => o.order_status === "confirmed").length}
                </div>
                <div className="text-xs text-gray-600">Active</div>
              </div>
              <div className="bg-white rounded-lg p-4 text-center shadow-lg">
                <div className="text-xl font-bold text-yellow-600">
                  {orders.filter((o) => o.order_status === "pending").length}
                </div>
                <div className="text-xs text-gray-600">Pending</div>
              </div>
            </div>
          )}

          {/* Mobile Actions */}
          <div className="lg:hidden mt-6 bg-white rounded-xl shadow-xl p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                type="primary"
                size="large"
                onClick={handleBookService}
                className="bg-gradient-to-r from-orange-500 to-red-500 border-none h-12"
              >
                Book New Service
              </Button>
              <Button
                size="large"
                onClick={() => navigate("/contact")}
                className="h-12"
              >
                Contact Support
              </Button>
            </div>
          </div>

          {/* Guest Account Creation Prompt - Mobile Optimized */}
          {userType === "guest" && orders.length > 0 && (
            <div className="mt-6 lg:mt-8 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4 lg:p-6">
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <User className="h-6 w-6 lg:h-8 lg:w-8 text-blue-600" />
                </div>
                <h3 className="text-base lg:text-lg font-semibold text-gray-800 mb-2">
                  💡 Save Your Order History
                </h3>
                <p className="text-sm lg:text-base text-gray-600 mb-4">
                  Create an account to easily track future orders and access
                  exclusive features.
                </p>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => navigate("/sign-up")}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 border-none h-12 px-6"
                >
                  Create Account
                </Button>
              </div>
            </div>
          )}

          {/* Compact Help Section */}
          <div className="mt-6 lg:mt-8 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 lg:p-6">
            <div className="text-center">
              <h3 className="text-base lg:text-lg font-semibold text-gray-800 mb-3">
                Need Help? 🙏
              </h3>
              <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                <Button
                  size="large"
                  onClick={() => window.open("tel:+919699588995", "_self")}
                  className="flex items-center justify-center h-12"
                >
                  📞 Call Support
                </Button>
                <Button
                  size="large"
                  onClick={() => navigate("/contact")}
                  className="flex items-center justify-center h-12"
                >
                  📧 Contact Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default MyOrders;
